import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, Trash2, Edit2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { confirmToast } from '../../utils/confirmToast';

// Utility function to truncate text (copied from BucketSection.tsx)
const truncateText = (text: string | undefined, maxLength: number = 150): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, text.lastIndexOf(' ', maxLength)) + '...';
};

interface SubBucket {
  _id: string;
  name: string;
  parent_bucket: string | { _id: string };
  image?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Bucket {
  _id: string;
  name: string;
  heading?: string;
  image: string;
  description?: string;
  isActive: boolean;
  subBuckets: SubBucket[];
  createdAt: string;
  updatedAt: string;
}

interface SubBucket {
  _id: string;
  name: string;
  parent_bucket: string | { _id: string };
  image?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const SubBucketsSection: React.FC = () => {
  const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null); // Added success message state
  const [expandedBuckets, setExpandedBuckets] = useState<{[key: string]: boolean}>({});
  const [showSubBucketForm, setShowSubBucketForm] = useState(false);
  const [editingSubBucket, setEditingSubBucket] = useState<SubBucket | null>(null);
  const [subBucketFormData, setSubBucketFormData] = useState({
    name: '',
    image: '',
    description: '',
    parentBucketId: '' // To store the parent bucket ID when adding a new sub-bucket
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null); // Added selectedFile state

  const subBucketFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showSubBucketForm && subBucketFormRef.current) {
      // Scroll to top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Also ensure the form element is in view
      subBucketFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showSubBucketForm]);

  // Scroll to edit form when editing starts
  useEffect(() => {
    if (editingSubBucket && subBucketFormRef.current) {
      // Add small delay to ensure DOM is updated
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        subBucketFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [editingSubBucket]);

  // Always reset state when opening add form to avoid stale state
  const openAddSubBucketForm = () => {
    setEditingSubBucket(null);
    setSubBucketFormData({
      name: '',
      image: '',
      description: '',
      parentBucketId: ''
    });
    setSelectedFile(null);
    setShowSubBucketForm(true);
  };

  // Open add form with parent bucket pre-selected
  const openAddSubBucketToParent = (parentBucketId: string) => {
    setEditingSubBucket(null);
    setSubBucketFormData({
      name: '',
      image: '',
      description: '',
      parentBucketId: parentBucketId
    });
    setSelectedFile(null);
    setShowSubBucketForm(true);
  };

  // Fetch buckets with their sub-buckets
  const fetchBuckets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First, fetch all buckets from buckets-updated endpoint
      const bucketsResponse = await fetch(`${API_BASE_URL}/api/buckets`);
      if (!bucketsResponse.ok) {
        throw new Error(`Failed to fetch buckets: ${bucketsResponse.statusText}`);
      }
      const bucketsData = await bucketsResponse.json();
      
      // Fetch sub-buckets from subbucketupdateds endpoint
      const subBucketsResponse = await fetch(`${API_BASE_URL}/api/subbuckets`);
      if (!subBucketsResponse.ok) {
        throw new Error(`Failed to fetch sub-buckets: ${subBucketsResponse.statusText}`);
      }
      const subBucketsData = await subBucketsResponse.json();
      
      // Handle different response structures
      const bucketsArray = Array.isArray(bucketsData) 
        ? bucketsData 
        : (bucketsData.data || bucketsData.buckets || []);
        
      const subBucketsArray = Array.isArray(subBucketsData)
        ? subBucketsData
        : (subBucketsData.data || subBucketsData.subBuckets || []);
      
      // Create a map of bucket IDs to buckets for easier lookup
      const bucketMap = new Map<string, Bucket>();
      
      // Initialize with existing buckets
      bucketsArray.forEach((bucket: Bucket) => {
        bucketMap.set(bucket._id, {
          ...bucket,
          subBuckets: []
        });
      });

      // Add all sub-buckets to their parent buckets
      subBucketsArray.forEach((subBucket: SubBucket) => {
        const parentId = typeof subBucket.parent_bucket === 'string' 
          ? subBucket.parent_bucket 
          : subBucket.parent_bucket?._id || '';
          
        if (!parentId) return;
          
        if (bucketMap.has(parentId)) {
          const bucket = bucketMap.get(parentId);
          if (bucket) {
            bucket.subBuckets = [...(bucket.subBuckets || []), subBucket];
          }
        }
        // If parent bucket doesn't exist, we simply skip this sub-bucket.
        // With hard deletes, associated sub-buckets should also be deleted.
        // If this block is still hit, it indicates orphaned sub-buckets in the DB.
        // For now, we prevent displaying them.
      });

      // Convert the map back to an array
      const bucketsWithSubs: Bucket[] = Array.from(bucketMap.values());
      setBuckets(bucketsWithSubs);
      
      // Initialize expanded state for each bucket
      const initialExpandedState: {[key: string]: boolean} = {};
      bucketsWithSubs.forEach((bucket: Bucket) => {
        initialExpandedState[bucket._id] = false;
      });
      setExpandedBuckets(initialExpandedState);
      
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBuckets();
  }, []);

  const toggleBucket = (bucketId: string) => {
    setExpandedBuckets(prev => ({
      ...prev,
      [bucketId]: !prev[bucketId]
    }));
  };

  const startEditSubBucket = (subBucket: SubBucket, parentBucketId: string) => {
    setEditingSubBucket(subBucket);
    setSubBucketFormData({
      name: subBucket.name,
      image: subBucket.image || '',
      description: subBucket.description || '',
      parentBucketId: parentBucketId
    });
    setSelectedFile(null); // Clear any previously selected file
    setShowSubBucketForm(true);
  };

  const deleteSubBucket = async (subBucketId: string, parentBucketId: string) => {
    const confirmed = await confirmToast('Are you sure you want to delete this sub-bucket?', {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/subbuckets/${subBucketId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        setBuckets(prevBuckets =>
          prevBuckets.map(bucket =>
            bucket._id === parentBucketId
              ? { ...bucket, subBuckets: bucket.subBuckets.filter(sub => sub._id !== subBucketId) }
              : bucket
          )
        );
        setSuccessMessage('Sub-bucket deleted successfully!'); // Set success message
        toast.success('Sub-bucket deleted');
      } else {
        setError(data.message || 'Failed to delete sub-bucket');
        toast.error(data.message || 'Failed to delete sub-bucket');
      }
    } catch (err) {
      setError('Error deleting sub-bucket');
      toast.error('Error deleting sub-bucket');
      console.error('Delete sub-bucket error:', err);
    }
  };

  const handleSubBucketSubmit = async (e: React.FormEvent, parentBucketId: string) => {
    e.preventDefault();
    setError(null);

    let finalImageUrl = subBucketFormData.image;

    // Validation: Ensure either a file is selected or a URL is entered
    if (selectedFile && subBucketFormData.image) {
      setError('Please either upload a file OR paste a URL, not both.');
      return;
    }
    
    if (!selectedFile && !subBucketFormData.image) {
      setError('You must upload an image or provide an image URL.');
      return;
    }

    // 1. If a file is selected, upload it first
    if (selectedFile) {
      const uploadFormData = new FormData();
      uploadFormData.append('image', selectedFile);

      try {
        const uploadRes = await fetch(`${API_BASE_URL}/api/upload`, {
          method: 'POST',
          body: uploadFormData,
        });
        const uploadData = await uploadRes.json();

        if (!uploadRes.ok || !uploadData.success) {
          throw new Error(uploadData.message || 'Image upload failed');
        }
        finalImageUrl = uploadData.imageUrl; // Get the URL from the backend
      } catch (err: any) {
        setError(err.message);
        return; // Stop if upload fails
      }
    }

    const method = editingSubBucket ? 'PUT' : 'POST';
    const url = editingSubBucket
      ? `${API_BASE_URL}/api/subbuckets/${editingSubBucket._id}`
      : `${API_BASE_URL}/api/subbuckets`;

    const payload = {
      name: subBucketFormData.name,
      image: finalImageUrl,
      description: subBucketFormData.description,
      parent_bucket: parentBucketId,
      isActive: true, // Sub-buckets are active by default when created/edited
    };

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        // Update the buckets state with the new/updated sub-bucket
        setBuckets(prevBuckets =>
          prevBuckets.map(bucket => {
            if (bucket._id === parentBucketId) {
              const updatedSubBuckets = editingSubBucket
                ? bucket.subBuckets.map(sub =>
                    sub._id === data.subBucket._id ? data.subBucket : sub
                  )
                : [...bucket.subBuckets, data.subBucket];
              return { ...bucket, subBuckets: updatedSubBuckets };
            }
            return bucket;
          })
        );
        setSuccessMessage(`Sub-bucket ${editingSubBucket ? 'updated' : 'created'} successfully!`); // Set success message
        cancelSubBucketForm();
      } else {
        setError(data.message || `Failed to ${editingSubBucket ? 'update' : 'create'} sub-bucket`);
      }
    } catch (err) {
      setError(`Error ${editingSubBucket ? 'updating' : 'creating'} sub-bucket`);
      console.error(`Sub-bucket ${editingSubBucket ? 'update' : 'create'} error:`, err);
    }
  };

  const cancelSubBucketForm = () => {
    setShowSubBucketForm(false);
    setEditingSubBucket(null);
    setSubBucketFormData({
      name: '',
      image: '',
      description: '',
      parentBucketId: ''
    });
    setSelectedFile(null); // Clear selected file
  };

  // Effect to clear success message after a few seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000); // Clear after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Effect to clear error message after a few seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 3000); // Clear after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  // Success Message
  if (successMessage) {
    return (
      <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
        <strong className="font-bold">Success: </strong>
        <span className="block sm:inline">{successMessage}</span>
        <button
          onClick={() => setSuccessMessage(null)}
          className="float-right font-bold text-green-700 hover:text-green-900"
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="font-poppins text-3xl font-bold text-gray-900">Sub-Buckets</h1>
          <p className="font-inter text-gray-600 mt-1">Manage sub-buckets within your buckets</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={openAddSubBucketForm}
          className="bg-primary text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 hover:bg-primary/90 transition-colors w-full sm:w-auto"
        >
          <span className="font-inter font-medium">Add Sub-Bucket</span>
        </motion.button>
      </motion.div>

      {/* Sub-Bucket Add/Edit Form - Moved to Top */}
      {showSubBucketForm && (
        <motion.div
          ref={subBucketFormRef}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mb-8"
        >
          <div className="mb-4">
            <h3 className="font-poppins text-lg font-semibold">
              {editingSubBucket ? 'Edit Sub-Bucket' : 'Add New Sub-Bucket'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {editingSubBucket ? 'Update the sub-bucket details below' : 'Fill in the details to create a new sub-bucket'}
            </p>
          </div>
          <form onSubmit={(e) => handleSubBucketSubmit(e, subBucketFormData.parentBucketId)} className="space-y-4">
            {!editingSubBucket && (
              <div>
                <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                  Parent Bucket <span className="text-red-500">*</span>
                </label>
                <select
                  value={subBucketFormData.parentBucketId}
                  onChange={(e) => setSubBucketFormData({ ...subBucketFormData, parentBucketId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                >
                  <option value="">Select a parent bucket</option>
                  {buckets.map((bucket) => (
                    <option key={bucket._id} value={bucket._id}>
                      {bucket.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                Sub-Bucket Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subBucketFormData.name}
                onChange={(e) => setSubBucketFormData({ ...subBucketFormData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter sub-bucket name"
                required
              />
            </div>
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                Sub-Bucket Image <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-2">
                <input 
                  type="url" 
                  placeholder="Paste Image URL"
                  value={subBucketFormData.image} 
                  onChange={(e) => {
                    setSubBucketFormData({ ...subBucketFormData, image: e.target.value });
                    setSelectedFile(null);
                  }} 
                  className="flex-grow px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" 
                  required={!selectedFile}
                />
                <span className="text-gray-500">OR</span>
                <label className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-inter cursor-pointer">
                  Upload
                  <input 
                    type="file" 
                    onChange={(e) => {
                      setSelectedFile(e.target.files ? e.target.files[0] : null);
                      setSubBucketFormData({ ...subBucketFormData, image: '' });
                    }} 
                    className="hidden" 
                    accept="image/*" 
                    required={!subBucketFormData.image}
                  />
                </label>
              </div>
              {selectedFile && <p className="text-sm text-gray-600 mt-2">File selected: {selectedFile.name}</p>}
            </div>
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                value={subBucketFormData.description}
                onChange={(e) => setSubBucketFormData({ ...subBucketFormData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={3}
              />
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors font-inter"
              >
                {editingSubBucket ? 'Update Sub-Bucket' : 'Create Sub-Bucket'}
              </button>
              <button
                type="button"
                onClick={cancelSubBucketForm}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors font-inter"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="space-y-4">
        {buckets.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No buckets found. Create a bucket first.</p>
          </div>
        ) : (
          buckets.map((bucket) => (
            <div key={bucket._id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <button
                onClick={() => toggleBucket(bucket._id)}
                className="w-full flex justify-between items-center p-4 text-left hover:bg-gray-50 focus:outline-none"
              >
                <div className="flex items-center">
                  <h3 className="text-lg font-medium text-gray-900">{bucket.name}</h3>
                  {bucket.heading && (
                    <span className="ml-2 text-sm text-gray-500">
                      ({bucket.heading})
                    </span>
                  )}
                </div>
                {expandedBuckets[bucket._id] ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </button>
              
              {expandedBuckets[bucket._id] && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4">
                    <button
                      onClick={() => openAddSubBucketToParent(bucket._id)}
                      className="mb-4 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors font-inter text-sm"
                    >
                      + Add Sub-Bucket
                    </button>
                    {bucket.subBuckets && bucket.subBuckets.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                        {bucket.subBuckets.map((subBucket, index) => (
                          <motion.div
                            key={subBucket._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="flex flex-col items-center group relative"
                          >
                            {/* Action Buttons */}
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => startEditSubBucket(subBucket, bucket._id)}
                                  className="bg-blue-500 text-white p-1 rounded hover:bg-blue-600 transition-colors"
                                  title="Edit sub-bucket"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => deleteSubBucket(subBucket._id, bucket._id)}
                                  className="bg-red-500 text-white p-1 rounded hover:bg-red-600 transition-colors"
                                  title="Delete sub-bucket"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>

                            {/* Sub-Bucket Image */}
                            <div className="w-full aspect-square">
                              <img
                                src={subBucket.image || '/api/placeholder/160/160'}
                                alt={subBucket.name}
                                className="w-full h-full object-cover rounded-lg"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/api/placeholder/160/160';
                                }}
                              />
                            </div>
                            
                            {/* Sub-Bucket Info */}
                            <div className="text-center mt-4 px-2">
                              <h4 className="font-inter font-medium text-xl md:text-2xl">{subBucket.name}</h4>
                              {subBucket.description && (
                                <p className="font-inter text-sm text-gray-600 mt-2 line-clamp-3">
                                  {truncateText(subBucket.description, 150)}
                                </p>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        No sub-buckets found for this bucket.
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SubBucketsSection;
